/**
 * The contact details an online order cannot be placed without — the ONE rule
 * the checkout form, its Pay button and its submit path all read.
 *
 * Email is REQUIRED. It is the only receipt channel that needs no opt-in: a
 * receipt text needs the guest's explicit consent (TCPA), which defaults off.
 * With email optional, a guest who skipped it and left the text box unticked
 * paid and received nothing at all — no receipt, no link back to the order.
 * House of Pho, 2026-09-25: order #30, $99.84 through Google Pay, no email,
 * text updates declined; the guest phoned the restaurant to ask whether the
 * order had gone through. Every other online order that week carried an email
 * and got its receipt.
 *
 * Phone was already required by the API (`customerInfo.phone`, minLength 10)
 * but never checked here, so a blank phone surfaced only as a failed submit.
 *
 * The API enforces the same two (routes/orders.ts, POST /checkout body
 * schema); this rule exists so the guest is told BEFORE paying — above all on
 * the wallet path, where Google/Apple Pay has already been authorized by the
 * time the submit runs.
 */
import { isCheckoutEmail, isCheckoutPhone } from '@order-up/common';
import type { CustomerInfo } from '../components/CustomerInformation';

/**
 * The SAME definition the API enforces (`@order-up/common` checkoutContact,
 * held to the route schema by a parity test) — a page looser than the API
 * would let a guest fill every field and still be refused at Pay.
 */
export function isValidCheckoutEmail(email: string | undefined | null): boolean {
    return isCheckoutEmail(email?.trim());
}

export function isValidCheckoutPhone(phone: string | undefined | null): boolean {
    return isCheckoutPhone(phone);
}

export type MissingContactField = 'phone' | 'firstName' | 'lastName' | 'email';

/** Which required contact fields are missing or malformed, in form order. */
export function missingContactFields(info: CustomerInfo): MissingContactField[] {
    const missing: MissingContactField[] = [];
    if (!isValidCheckoutPhone(info.phone)) missing.push('phone');
    if (!info.firstName?.trim()) missing.push('firstName');
    if (!info.lastName?.trim()) missing.push('lastName');
    if (!isValidCheckoutEmail(info.email)) missing.push('email');
    return missing;
}

/** The one sentence a guest sees when contact details stop the order. */
export function contactFieldsMessage(missing: readonly MissingContactField[]): string {
    if (missing.length === 1 && missing[0] === 'email') {
        return 'Please enter a valid email address — we send your receipt there.';
    }
    if (missing.length === 1 && missing[0] === 'phone') {
        return 'Please enter a 10-digit mobile number so the restaurant can reach you.';
    }
    return 'Please fill in your mobile number, name and email.';
}
